import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { apiFetch, safeJson } from "@/lib/api";

const AuthContext = createContext({
  token: null,
  isAdmin: false,
  profileComplete: false,
  setProfileComplete: () => {},
  apiFetch: async () => new Response(),
});

export function AuthProvider({ token, isAdmin, initialProfileComplete, children }) {
  const [profileComplete, setProfileComplete] = useState(
    isAdmin ? true : initialProfileComplete || false
  );
  // True once we KNOW the profile state (admin, or fresh-login already-complete, or the
  // /profile check returned). Used to gate the entry spinner instead of a fixed 1.2s timer.
  const [profileLoaded, setProfileLoaded] = useState(
    isAdmin || !!initialProfileComplete || !token
  );

  const boundFetch = useCallback((path, options) => apiFetch(path, options), []);

  useEffect(() => {
    if (isAdmin) {
      setProfileComplete(true);
      setProfileLoaded(true);
      return;
    }
    if (!token) {
      setProfileComplete(false);
      setProfileLoaded(true);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch("/api/user/profile");
        const data = await safeJson(res);
        if (data) setProfileComplete(data.profile_complete || false);
      } catch (e) {
        console.debug("Profile check failed (keeping current state):", e);
      } finally {
        setProfileLoaded(true);
      }
    })();
  }, [token, isAdmin]);

  // Memoized so consumers don't re-render unless an actual auth value changes.
  const value = useMemo(
    () => ({ token, isAdmin, profileComplete, setProfileComplete, profileLoaded, apiFetch: boundFetch }),
    [token, isAdmin, profileComplete, profileLoaded, boundFetch]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
